import React, { useState, useEffect, useContext } from 'react';
import { Card, Form, Button, Badge } from 'react-bootstrap';
import { Key, Plus, Trash2, Copy, Check, Info, Calendar, Clock, Lock } from 'lucide-react';
import {
    listApiKeys,
    createApiKey,
    deleteApiKey
} from '../../../usecases/api-keys-usecase';
import { dispatchCustomEvent } from '../../../utils/dispatch';
import { roleContext } from '../../../context/role/context';
import ButtonLoading from '../../atoms/buttonLoading/view';
import ModalBtn from '../../molecules/modalButton/view';
import './ApiKeyManager.css';
import '../../styles/shared-redesign.css';

const ApiKeyManager = () => {
    const { profile } = useContext(roleContext);
    const [apiKeys, setApiKeys] = useState([]);
    const [loading, setLoading] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [newKeyData, setNewKeyData] = useState(null);
    const [copySuccess, setCopySuccess] = useState(false);

    // Form states
    const [keyName, setKeyName] = useState('');
    const [expiresIn, setExpiresIn] = useState(180);

    useEffect(() => {
        fetchApiKeys();
    }, [profile?.id]);

    const fetchApiKeys = async () => {
        if (!profile) return;
        setLoading(true);
        try {
            const response = await listApiKeys(profile.uid, profile.cid);
            setApiKeys(response.data || []);
        } catch (err) {
            console.error(err);
            dispatchCustomEvent('popup', { message: '❌ Error al cargar API Keys', type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleCreateKey = async () => {
        if (!keyName) return;

        setActionLoading(true);
        try {
            const result = await createApiKey(profile.uid, profile.cid, {
                name: keyName,
                expires_in_days: expiresIn
            });
            setNewKeyData(result.data);
            setKeyName('');
            setExpiresIn(180);
            fetchApiKeys();
            // Don't close modal automatically if we have a new key to show
        } catch (err) {
            dispatchCustomEvent('popup', { message: `❌ Error al crear API Key`, type: 'error' });
        } finally {
            setActionLoading(false);
            dispatchCustomEvent('loading-btn-create-api-key');
        }
    };

    const handleDeleteKey = async (id) => {
        if (!window.confirm('¿Estás seguro de que deseas eliminar esta API Key? Las aplicaciones que la usen dejarán de funcionar.')) return;
        
        try {
            await deleteApiKey(profile.uid, profile.cid, id);
            dispatchCustomEvent('popup', { message: '✅ API Key eliminada', type: 'success' });
            fetchApiKeys();
        } catch (err) {
            dispatchCustomEvent('popup', { message: '❌ Error al eliminar API Key', type: 'error' });
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
        dispatchCustomEvent('popup', { message: '✅ Token copiado al portapapeles', type: 'success' });
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Nunca';
        return new Date(dateString).toLocaleDateString('es-CO', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <div className="api-key-manager">
            <div className="api-key-header">
                <ModalBtn
                    id="create-api-key"
                    type="pill-primary"
                    title="Nueva API Key"
                    icon={<Plus size={18} />}
                    onClose={() => setNewKeyData(null)}
                >
                    <div className="redesign-modal-container">
                        {!newKeyData ? (
                            <>
                                <div className="redesign-modal-header">
                                    <h3 className="redesign-modal-title">Generar Nueva API Key</h3>
                                    <p className="redesign-modal-desc">
                                        Genera una llave de acceso para integrar EasyPOS con otras aplicaciones o servicios externos.
                                    </p>
                                </div>

                                <Form className="redesign-modal-body">
                                    <Form.Group className="mb-3">
                                        <Form.Label className="redesign-label">Nombre de la Llave</Form.Label>
                                        <div className="redesign-input-wrapper">
                                            <Key className="redesign-input-icon" size={20} />
                                            <Form.Control
                                                type="text"
                                                placeholder="Ej. Integración E-commerce"
                                                className="redesign-input"
                                                value={keyName}
                                                onChange={(e) => setKeyName(e.target.value)}
                                                required
                                            />
                                        </div>
                                    </Form.Group>

                                    <Form.Group className="mb-4">
                                        <Form.Label className="redesign-label">Expira en (días)</Form.Label>
                                        <div className="redesign-input-wrapper">
                                            <Calendar className="redesign-input-icon" size={20} />
                                            <Form.Select
                                                className="redesign-input"
                                                value={expiresIn}
                                                onChange={(e) => setExpiresIn(parseInt(e.target.value))}
                                            >
                                                <option value={30}>30 días</option>
                                                <option value={90}>90 días</option>
                                                <option value={180}>180 días</option>
                                                <option value={365}>1 año</option>
                                            </Form.Select>
                                        </div>
                                    </Form.Group>

                                    <ButtonLoading
                                        id="create-api-key"
                                        className="w-100 btn-pill btn-pill-primary"
                                        icon={<Plus size={18} />}
                                        title="Generar API Key"
                                        onClick={handleCreateKey}
                                    />
                                </Form>
                            </>
                        ) : (
                            <div className="api-key-result-container">
                                <div className="text-center mb-4">
                                    <div className="success-icon-wrapper mb-3">
                                        <Check size={48} className="text-success" />
                                    </div>
                                    <h3 className="redesign-modal-title">¡API Key Generada!</h3>
                                    <p className="text-danger fw-bold">
                                        Copia esta llave ahora. Por seguridad, no podrás volver a verla.
                                    </p>
                                </div>

                                <div className="token-display-box">
                                    <code>{newKeyData.token}</code>
                                    <button 
                                        className="copy-token-btn"
                                        onClick={() => copyToClipboard(newKeyData.token)}
                                    >
                                        {copySuccess ? <Check size={20} /> : <Copy size={20} />}
                                    </button>
                                </div>

                                <div className="mt-4 alert alert-warning d-flex align-items-start gap-2">
                                    <Lock size={24} className="flex-shrink-0" />
                                    <small>
                                        Esta llave otorga acceso total a tus operaciones de venta y productos. 
                                        Trátala como una contraseña y asegúrate de guardarla en un lugar seguro.
                                    </small>
                                </div>

                                <Button 
                                    variant="primary" 
                                    className="w-100 mt-3"
                                    onClick={() => dispatchCustomEvent('close-create-api-key-modal')}
                                >
                                    He guardado la llave
                                </Button>
                            </div>
                        )}
                    </div>
                </ModalBtn>
            </div>

            <Card className="api-keys-main-card mt-4">
                <Card.Header className="bg-transparent border-0 pt-4 px-4 pb-0 d-flex justify-content-between align-items-center">
                    <h5 className="fw-bold m-0">Mis API Keys</h5>
                    <Badge bg="info" className="opacity-75">
                        <Info size={14} className="me-1" />
                        Acceso Programático
                    </Badge>
                </Card.Header>
                <Card.Body className="px-0">
                    {loading ? (
                        <div className="text-center py-5">Cargando llaves...</div>
                    ) : apiKeys.length === 0 ? (
                        <div className="text-center py-5 text-muted">
                            <Key size={40} className="mb-2 opacity-50" />
                            <p>No has generado ninguna API Key aún.</p>
                        </div>
                    ) : (
                        <div className="api-keys-list">
                            <div className="api-keys-table-header px-4 py-2 border-bottom d-none d-md-grid">
                                <div>Nombre</div>
                                <div>Prefijo</div>
                                <div>Último Uso</div>
                                <div>Expira</div>
                                <div className="text-end">Acciones</div>
                            </div>
                            {apiKeys.map((key) => (
                                <div key={key.id} className="api-key-row px-4 py-3 border-bottom">
                                    <div className="key-name-col">
                                        <span className="fw-bold d-block d-md-none small text-muted mb-1">Nombre</span>
                                        <div className="d-flex align-items-center gap-2">
                                            <div className="key-icon-small">
                                                <Key size={14} />
                                            </div>
                                            <span className="fw-medium">{key.name}</span>
                                        </div>
                                    </div>
                                    <div className="key-prefix-col">
                                        <span className="fw-bold d-block d-md-none small text-muted mb-1">Prefijo</span>
                                        <code>{key.prefix}...</code>
                                    </div>
                                    <div className="key-usage-col">
                                        <span className="fw-bold d-block d-md-none small text-muted mb-1">Último Uso</span>
                                        <span className="text-muted d-flex align-items-center gap-1">
                                            <Clock size={12} />
                                            {formatDate(key.last_used_at)}
                                        </span>
                                    </div>
                                    <div className="key-expiry-col">
                                        <span className="fw-bold d-block d-md-none small text-muted mb-1">Expira</span>
                                        <span className={new Date(key.expires_at) < new Date() ? 'text-danger' : ''}>
                                            {formatDate(key.expires_at)}
                                        </span>
                                    </div>
                                    <div className="text-end">
                                        <Button
                                            variant="outline-danger"
                                            size="sm"
                                            className="btn-delete-key"
                                            onClick={() => handleDeleteKey(key.id)}
                                        >
                                            <Trash2 size={16} />
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </Card.Body>
            </Card>
        </div>
    );
};

export default ApiKeyManager;
